// Shared event timeline (spec 17 §4) — used by /events (merged, multi-device)
// and /events/:deviceId (single device). Handles: date-range picking, type
// filter chips (client-side cluster filtering, see eventsLib), cursor-based
// infinite scroll, INVALID_CURSOR restart, click → playback seek, and the
// W3/APP-01 realtime path: WSS room 'event' frames merge in newest-first
// (deduped vs REST rows), with a reconnect-triggered refetch to reconcile
// anything missed while offline (specs 16 §5.2 / 17 §4.1).

import { MuteRowActions, MutedHiddenLine } from './MuteZoneUi';
import { isMuted } from './muteZones';
import { ClusterIcon } from '../../components/ClusterIcon';
import { camZone } from '../../lib/camTz';
import { eventAiLine } from '../../lib/eventAi';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { signaling } from '../../lib/ws';
import {
  clusterOfEvent,
  dayWindowUs,
  describeEvent, isUnconfirmed,
  fetchDeviceEvents,
  loadEventSnapshot,
  forgetEventSnapshot,
  toDateInputValue,
  type ClusterInfo,
  type ClusterKey,
  type EventItem,
  type TimelineRow,
  type UsWindow,
  eventStorage,
  eventStorageMixed,
  type EventStorage,
} from './eventsLib';
import { purgeExpiredSnapshots } from './eventSnapshotDb';
import { eventKey, mergeTimeline, onLiveEvent, readEvents } from './liveEvents';
import { getEvents, purgeOlderThan, putEvents } from './eventsDb';
import { Illustration } from '../../components/Illustration';
import './events.css';

export interface TimelineDevice {
  id: string;
  name: string;
  /** 目前是否在線（tileState !== 'offline'）；缺值視為在線。SD 卡事件遇離線 → 角標轉灰。 */
  online?: boolean;
}

interface DeviceFeed {
  items: EventItem[];
  cursor: string | null;
  /** No further pages for the current window. */
  done: boolean;
  loading: boolean;
  error: string | null;
}

type RangeKey = 'recent' | '7d' | 'today' | 'custom';

const PAGE_LIMIT = 100;
// 'recent' (default): last RECENT_HOURS up to now, with an open tail so live events
// pass the merge-window gate. The events API pages ASCENDING (oldest-first), so the
// newest events only surface after the whole window is loaded — the auto-drain effect
// loads this bounded window fully (capped) so the newest sit at the top immediately.
const RECENT_HOURS = 3;
const RECENT_DRAIN_MAX_PAGES = 20;
// 離線快取 90 天自清視窗(µs):對齊 server ~90d 事件保存 horizon。載入時把
// event_ts 早於 (now - 90d) 的快取事件連同其縮圖刪除(Phase C，見 eventsDb）。
const NINETY_DAYS_US = 90 * 86_400_000_000;

function freshFeed(): DeviceFeed {
  return { items: [], cursor: null, done: false, loading: false, error: null };
}

function rangeWindow(range: RangeKey, customDate: string): UsWindow {
  if (range === 'custom') return dayWindowUs(customDate);
  const today = dayWindowUs(toDateInputValue(new Date()));
  if (range === 'today') return today;
  if (range === 'recent') {
    const nowUs = Date.now() * 1000;
    return { fromUs: nowUs - RECENT_HOURS * 3_600_000_000, toUs: nowUs + 86_400_000_000 };
  }
  return { fromUs: today.fromUs - 6 * 86_400_000_000, toUs: today.toUs };
}

// 事件縮圖:交付「跟 clip 影片一樣」——has_snapshot 為 true 時呼叫 per-event snapshot 端點拿
// 一枚 URL,直接 `<img src={url}>`。web client 拿到的是**同源 proxy** URL(/v3/recordings/download,
// restful 從 snapshot 桶串流)—— 瀏覽器 CSP 禁止直連跨來源 S3,故走同源,與 `<video src>` 對稱
// 縮圖 src 由 loadEventSnapshot 解析:記憶體 → IndexedDB(本地持久,90 天自刪)→ 網路
// (抓 bytes 存成 data: URL;img-src 有 data: 無 blob:)。抓不到 bytes 時退回短效簽名 URL,
// 縮圖不消失。載入中 / 無縮圖 / 載入失敗 → 退回型別 icon。onError 只清該 eid、不重抓(setUrl(null)
// 不在 effect deps → 不會有「抓→404→重抓」的閃爍迴圈)。
// 錄影位置角標（業主 2026-09-08 裁決 A）：縮圖右下角深色圓底 + 白色雲朵（雲端）/ 記憶卡（SD 卡）。
// 呼叫端只在混存時才傳 storage（見 eventStorageMixed）。SD 卡事件遇攝影機離線 → 圓底轉灰。
// 鏡像 Android `StorageBadge` / iOS `StorageBadgeView`。
function StorageBadge({ storage, offline }: { storage: EventStorage; offline: boolean }) {
  const { t } = useTranslation();
  const sd = storage === 'sdcard';
  const label = sd ? (offline ? t('events.storageSdcardOffline') : t('events.storageSdcard')) : t('events.storageCloud');
  return (
    <span className={`evt-storage${sd && offline ? ' evt-storage-offline' : ''}`} role="img" aria-label={label} title={label} data-storage={storage}>
      {sd ? (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M8 2h8a2 2 0 012 2v16a2 2 0 01-2 2H6a2 2 0 01-2-2V8l4-6zm2 3v4h1.5V5H10zm3 0v4h1.5V5H13z" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M6.5 19a4.5 4.5 0 01-.6-8.96A6 6 0 0117.7 8.6 4 4 0 0117.5 19z" /></svg>
      )}
    </span>
  );
}

export function EventThumb({ deviceId, ev, cluster, storage, offline }: { deviceId: string; ev: EventItem; cluster: ClusterInfo; storage?: EventStorage | null; offline?: boolean }) {
  const eid = ev.eid;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!eid || !ev.has_snapshot) return;
    const ctrl = new AbortController();
    let alive = true;
    void loadEventSnapshot(deviceId, eid, ctrl.signal).then((u) => {
      if (alive && u) setUrl(u);
    });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [deviceId, eid, ev.has_snapshot]);
  return (
    <span className={`evt-thumb evt-thumb-${cluster.key}`} aria-hidden>
      {url ? (
        <img className="evt-thumb-img" src={url} alt="" onError={() => { if (eid) forgetEventSnapshot(eid); setUrl(null); }} />
      ) : (
        <ClusterIcon kind={cluster.key} />
      )}
      {storage ? <StorageBadge storage={storage} offline={offline === true} /> : null}
    </span>
  );
}

export function EventTimeline({ devices }: { devices: TimelineDevice[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeKey>('recent');
  const [customDate, setCustomDate] = useState(() => toDateInputValue(new Date()));
  const [feeds, setFeeds] = useState<Record<string, DeviceFeed>>({});
  const [activeCluster, setActiveCluster] = useState<ClusterKey | 'all'>('all');
  const [showMuted, setShowMuted] = useState(false); // 不通知區（docs/mute-zones.md §11-3）：預設不列
  const [notice, setNotice] = useState<string | null>(null);
  // W3/APP-01：WSS room 即時插入的事件列（REST 對帳後同 key 由 REST 取代）
  const [liveRows, setLiveRows] = useState<TimelineRow[]>([]);
  // 重連對帳：reconnecting→open 時 bump，觸發整窗 refetch 補斷線期間的洞
  const [reloadKey, setReloadKey] = useState(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // generation guard: window/device changes invalidate in-flight loads
  const genRef = useRef(0);

  const win = useMemo(() => rangeWindow(range, customDate), [range, customDate]);
  const deviceKey = devices.map((d) => d.id).join(',');

  // 本地縮圖 90 天自刪:開頁清一次過期的 IndexedDB 快取(best-effort,無 IndexedDB 時 no-op)。
  useEffect(() => {
    void purgeExpiredSnapshots();
  }, []);

  const loadPage = useCallback(
    async (deviceId: string, cursor: string | null, gen: number) => {
      setFeeds((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] ?? freshFeed()), loading: true, error: null } }));
      try {
        const res = await fetchDeviceEvents(deviceId, { from: win.fromUs, to: win.toUs, cursor, limit: PAGE_LIMIT });
        if (gen !== genRef.current) return;
        // 磁碟落地(離線快取):抓到的列 upsert 進 IndexedDB(以 eventKey 去重覆蓋)。
        void putEvents(deviceId, res.items);
        setFeeds((prev) => {
          const cur = prev[deviceId] ?? freshFeed();
          // upsert 去重(eventKey 身分):保留既有(含離線快取 seed / 前頁),抓到的列覆蓋同 key。
          // 不再對第一頁 setFeeds({}) 清空——offline-first 的 cached seed 必須留住,
          // 且同一事件既在快取又被抓到時不重複列出(dedup by eventKey)。
          const map = new Map<string, EventItem>();
          for (const e of cur.items) map.set(eventKey(deviceId, e), e);
          for (const e of res.items) map.set(eventKey(deviceId, e), e);
          return {
            ...prev,
            [deviceId]: {
              items: [...map.values()],
              cursor: res.nextCursor,
              done: res.nextCursor === null || res.items.length === 0,
              loading: false,
              error: null,
            },
          };
        });
      } catch (e) {
        if (gen !== genRef.current) return;
        if (e instanceof ApiError && e.code === 'INVALID_CURSOR') {
          // cursor TTL expired (restful 00 A.3) → restart from the first page (spec 17 B2)
          setNotice(t('events.cursorExpired'));
          setFeeds((prev) => ({ ...prev, [deviceId]: freshFeed() }));
          void loadPage(deviceId, null, gen);
          return;
        }
        const msg = e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkError');
        setFeeds((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] ?? freshFeed()), loading: false, error: msg } }));
      }
    },
    [win.fromUs, win.toUs, t]
  );

  // Reset + first page whenever the device set / time window changes, or a
  // reconnect demands reconciliation (reloadKey). liveRows survive a reconcile
  // (REST duplicates win via mergeTimeline) but reset with window/device change.
  const prevScopeRef = useRef('');
  useEffect(() => {
    const gen = ++genRef.current;
    const scope = `${deviceKey}|${win.fromUs}|${win.toUs}`;
    if (prevScopeRef.current !== scope) {
      prevScopeRef.current = scope;
      setLiveRows([]);
    }
    setFeeds({});
    setNotice(null);
    // Phase C 90d 自清:先刪 event_ts 早於 (now - 90d) 的快取事件 + 其縮圖(機會式,
    // 於載入時跑,不需背景排程),對齊 server ~90d 保存 horizon。
    void purgeOlderThan(Date.now() * 1000 - NINETY_DAYS_US);
    const from = win.fromUs;
    const to = win.toUs;
    for (const d of devices) {
      // offline-first:先讀本機快取(僅落在目前視窗者)即刻 seed 進 feed,讓事件在
      // 網路慢 / 離線時就先顯示;隨後的 fetchDeviceEvents 以 eventKey 去重 upsert,
      // 不重複列出、fetch 失敗也不清掉已顯示的快取列。
      void getEvents(d.id).then((cached) => {
        if (gen !== genRef.current || cached.length === 0) return;
        const inWin = cached.filter((e) => e.event_ts >= from && e.event_ts < to);
        if (inWin.length === 0) return;
        setFeeds((prev) => {
          const cur = prev[d.id] ?? freshFeed();
          // 快取列墊底,既有(網路 / live)列覆蓋同 key —— 線上資料權威。
          const map = new Map<string, EventItem>();
          for (const e of inWin) map.set(eventKey(d.id, e), e);
          for (const e of cur.items) map.set(eventKey(d.id, e), e);
          return { ...prev, [d.id]: { ...cur, items: [...map.values()] } };
        });
      });
      void loadPage(d.id, null, gen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, win.fromUs, win.toUs, reloadKey]);

  // W3/APP-01 前景即時：room 廣播 'event' → 落在目前窗口且屬於本軸裝置 →
  // 以 eventKey 去重後插入（同一 frame 重複廣播不重列）；排序與 REST 同路徑。
  useEffect(() => {
    const ids = new Set(devices.map((d) => d.id));
    return onLiveEvent(({ deviceId, ev }) => {
      if (!ids.has(deviceId)) return;
      if (ev.event_ts < win.fromUs || ev.event_ts >= win.toUs) return;
      setLiveRows((prev) => {
        const k = eventKey(deviceId, ev);
        if (prev.some((r) => eventKey(r.deviceId, r.ev) === k)) return prev;
        // cap：防長掛頁面無限成長（REST 對帳後重複者本來就會被取代）
        return [...prev.slice(-199), { deviceId, ev }];
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey, win.fromUs, win.toUs]);

  // 斷線補洞：WSS reconnecting→open（斷線期間 room 廣播必然漏）→ refetch 對帳。
  useEffect(() => {
    let prev = signaling.getState();
    return signaling.on('state', (s) => {
      const was = prev;
      prev = s;
      if (s === 'open' && was === 'reconnecting') setReloadKey((k) => k + 1);
    });
  }, []);

  const loadMore = useCallback(() => {
    const gen = genRef.current;
    for (const d of devices) {
      const f = feeds[d.id];
      if (f && !f.done && !f.loading && !f.error && f.cursor) void loadPage(d.id, f.cursor, gen);
    }
  }, [devices, feeds, loadPage]);

  const anyLoading = devices.some((d) => feeds[d.id]?.loading);
  const hasMore = devices.some((d) => {
    const f = feeds[d.id];
    return !f || (!f.done && !f.error);
  });

  // Infinite scroll sentinel.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) loadMore();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  // Auto-drain the bounded 'recent' window so the NEWEST events reach the top
  // without manual scroll (the server pages ascending = oldest-first, so the newest
  // sit on the LAST page). Capped by RECENT_DRAIN_MAX_PAGES to avoid a runaway on an
  // unexpectedly dense window; other ranges keep manual infinite-scroll.
  const drainRef = useRef(0);
  useEffect(() => { drainRef.current = 0; }, [range, win.fromUs, win.toUs, reloadKey]);
  useEffect(() => {
    if (range !== 'recent' || !hasMore || anyLoading) return;
    if (drainRef.current >= RECENT_DRAIN_MAX_PAGES) return;
    drainRef.current += 1;
    loadMore();
  }, [range, hasMore, anyLoading, loadMore, win.fromUs, win.toUs, reloadKey]);

  // Merge + sort NEWEST-FIRST（W5/APP-03，restful 00 §13「client 倒序顯示自行
  // 處理」）：最新事件固定在頂；server 升冪 cursor 翻頁所得（較新）資料與即時
  // WSS 事件（liveRows，W3/APP-01）走同一排序路徑插入，舊資料自然沉底，
  // 「載入更多」鈕留在列表底部。REST 與 live 同 key 時 REST 權威（對帳取代）。
  const nameOf = useMemo(() => new Map(devices.map((d) => [d.id, d.name])), [devices]);
  const merged = useMemo(() => {
    const rows: TimelineRow[] = [];
    for (const d of devices) {
      for (const ev of feeds[d.id]?.items ?? []) rows.push({ deviceId: d.id, ev });
    }
    return mergeTimeline(rows, liveRows);
  }, [devices, feeds, liveRows]);

  // Type filter chips — only clusters present in loaded data (spec 17 §4.2).
  // W2-03：型別判定餵 event_id（hex cluster bits），event_type（ch19 語意字串）
  // 僅 fallback——餵錯欄位曾使 BigInt 解析全炸、所有事件標「其他」。
  const presentClusters = useMemo(() => {
    const seen = new Map<ClusterKey, { label: string; icon: string }>();
    for (const r of merged) {
      const c = clusterOfEvent(r.ev);
      if (!seen.has(c.key)) seen.set(c.key, { label: c.label, icon: c.icon });
    }
    return [...seen.entries()];
  }, [merged]);

  const mutedCount = useMemo(() => merged.filter((r) => isMuted(r.ev)).length, [merged]);
  const visible = useMemo(
    () =>
      (activeCluster === 'all' ? merged : merged.filter((r) => clusterOfEvent(r.ev).key === activeCluster)).filter(
        (r) => isMuted(r.ev) === showMuted
      ),
    [merged, activeCluster, showMuted]
  );
  // 錄影位置角標只在「混存」時出現（業主 2026-09-08 裁決 A；spec 17 §4）：顯示中的事件同時有
  // 雲端與 SD 卡兩種才標，全是同一種就不標。
  const storageMixed = useMemo(() => eventStorageMixed(visible.map((r) => r.ev)), [visible]);
  const offlineOf = useMemo(() => new Map(devices.map((d) => [d.id, d.online === false])), [devices]);

  // per-item 已讀狀態（APP#10）：store 變更 → readVer 遞增 → 重繪。未讀數以目前
  // 可見列計（隨類型/區間篩選變動）。「全部標為已讀」標 merged 全列（含被篩掉的）。
  const readVer = useSyncExternalStore(readEvents.subscribe, readEvents.getVersion);
  const visibleKeys = useMemo(() => visible.map((r) => eventKey(r.deviceId, r.ev)), [visible]);
  const unreadCount = useMemo(() => readEvents.unreadCount(visibleKeys), [visibleKeys, readVer]);
  const markAllRead = useCallback(() => {
    readEvents.markRead(...merged.map((r) => eventKey(r.deviceId, r.ev)));
  }, [merged]);

  const errors = devices
    .map((d) => ({ d, err: feeds[d.id]?.error }))
    .filter((x): x is { d: TimelineDevice; err: string } => Boolean(x.err));

  const initialLoading = merged.length === 0 && anyLoading;

  return (
    <div className="evt-timeline">
      <div className="evt-toolbar">
        <div className="evt-chips" role="group" aria-label={t('events.timeRange')}>
          <button className={`evt-chip ${range === 'recent' ? 'active' : ''}`} onClick={() => setRange('recent')}>
            {t('events.rangeRecent', { defaultValue: '最近' })}
          </button>
          <button className={`evt-chip ${range === '7d' ? 'active' : ''}`} onClick={() => setRange('7d')}>
            {t('events.range7d')}
          </button>
          <button className={`evt-chip ${range === 'today' ? 'active' : ''}`} onClick={() => setRange('today')}>
            {t('events.rangeToday')}
          </button>
          <button className={`evt-chip ${range === 'custom' ? 'active' : ''}`} onClick={() => setRange('custom')}>
            {t('events.rangeCustom')}
          </button>
          {range === 'custom' && (
            <input type="date" className="evt-date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} aria-label={t('events.selectDate')} />
          )}
        </div>
        {presentClusters.length > 0 && (
          <div className="evt-chips" role="group" aria-label={t('events.eventType')}>
            <button className={`evt-chip ${activeCluster === 'all' ? 'active' : ''}`} onClick={() => setActiveCluster('all')}>
              {t('events.allTypes')}
            </button>
            {presentClusters.map(([key, info]) => (
              <button key={key} className={`evt-chip ${activeCluster === key ? 'active' : ''}`} onClick={() => setActiveCluster(key)}>
                {info.icon} {info.label}
              </button>
            ))}
          </div>
        )}
        {unreadCount > 0 && (
          <button className="btn-ghost evt-markall" data-testid="evt-mark-all-read" onClick={markAllRead}>
            {t('events.markAllRead', { count: unreadCount })}
          </button>
        )}
      </div>

      {notice && (
        <p className="evt-notice" role="status">
          {notice}
        </p>
      )}
      {errors.map(({ d, err }) => (
        <div key={d.id} className="evt-error card" role="alert">
          <span className="error-text">
            {devices.length > 1 ? `${d.name}：` : ''}
            {t('events.loadEventsFailed', { err })}
          </span>
          <button
            className="btn-ghost evt-retry"
            onClick={() => {
              setFeeds((prev) => ({ ...prev, [d.id]: freshFeed() }));
              void loadPage(d.id, null, genRef.current);
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      ))}

      {initialLoading && <p className="text-secondary evt-state">{t('events.loadingEvents')}</p>}

      {!initialLoading && visible.length === 0 && errors.length === 0 && !anyLoading && (
        <div className="card evt-empty" style={{ textAlign: 'center' }}>
          <Illustration name="no-events" alt="" size={160} />
          <p style={{ margin: 0 }}>{t('empty.noEventsInRange')}</p>
          <p className="text-secondary" style={{ marginBottom: 0 }}>
            {t('empty.noEventsHint')}
          </p>
        </div>
      )}

      <MutedHiddenLine count={mutedCount} showing={showMuted} onToggle={() => setShowMuted((v) => !v)} />
      <ul className="evt-list">
        {visible.map(({ deviceId, ev }) => {
          const c = clusterOfEvent(ev);
          const desc = describeEvent(ev);
          const key = eventKey(deviceId, ev);
          const unread = !readEvents.isRead(key);
          return (
            <li key={`${deviceId}-${ev.event_ts}-${ev.correlation_id ?? ev.event_id}`}>
              <button
                className={`evt-row${unread ? ' unread' : ''}`}
                onClick={() => {
                  readEvents.markRead(key); // 開回放即視為已讀（APP#10）
                  navigate(`/playback/${encodeURIComponent(deviceId)}?t=${ev.event_ts}`);
                }}
                title={t('events.viewEventPlayback')}
              >
                {/* 未讀圓點（APP#10）*/}
                <span className="evt-unread-dot" data-unread={unread} aria-label={unread ? t('events.unread') : undefined} />
                {/* 縮圖：has_snapshot 時懶拉 snapshot_url 顯示縮圖，否則退回型別 icon
                    （漸層底色 placeholder，GAP-PB-2 / spec 17 §4.1 定版降級） */}
                <EventThumb deviceId={deviceId} ev={ev} cluster={c} storage={storageMixed ? eventStorage(ev) : null} offline={offlineOf.get(deviceId) === true} />
                <span className="evt-row-main">
                  <span className="evt-row-title">
                    {c.label}
                    {isUnconfirmed(ev) ? <span className="evt-unconf" title={t('events.unconfirmedHint')}>{t('events.unconfirmed')}</span> : null}
                    {desc ? <span className="text-secondary evt-row-desc"> {desc}</span> : null}
                  </span>
                  <span className="text-secondary evt-row-sub">
                    {devices.length > 1 ? `${nameOf.get(deviceId) ?? deviceId} · ` : ''}
                    {formatTimestampUs(ev.event_ts, { timeZone: camZone(deviceId) })}
                  </span>
                  {(() => {
                    const ai = eventAiLine(t, ev);
                    return ai ? (
                      <span className={`evt-row-ai${ai.confirmed ? ' evt-row-ai-confirmed' : ''}`} data-testid="event-ai">
                        {ai.text}
                      </span>
                    ) : null;
                  })()}
                </span>
                <span className="evt-row-cta">{t('events.watchPlayback')}</span>
              </button>
              <MuteRowActions deviceId={deviceId} ev={ev} onChanged={() => setReloadKey((k) => k + 1)} />
            </li>
          );
        })}
      </ul>

      <div ref={sentinelRef} />
      {hasMore && merged.length > 0 && (
        <button className="btn-ghost evt-more" onClick={loadMore} disabled={anyLoading}>
          {anyLoading ? t('common.loading') : t('common.loadMore')}
        </button>
      )}
    </div>
  );
}
